import {
  createProgressiveRequestHandler,
  createRequestHandler,
} from "../utils/requestHandlers";

import { cfAPI } from "../api";
import { createFormData } from "../utils/onCreateFormData";
import { createProgressHandler } from "../utils/createProgressHandler";

// Action Types
const EXPERIENCE_REQUEST = "EXPERIENCE_REQUEST";
const EXPERIENCE_ERROR = "EXPERIENCE_ERROR";
const EXPERIENCES_FETCH = "EXPERIENCES_FETCH";
const EXPERIENCE_FETCH = "EXPERIENCE_FETCH";
const EXPERIENCE_CREATE = "EXPERIENCE_CREATE";
const EXPERIENCE_EDIT = "EXPERIENCE_EDIT";
const EXPERIENCE_DELETE = "EXPERIENCE_DELETE";
const EXPERIENCE_REORDER_LOCAL = "EXPERIENCE_REORDER_LOCAL";

// Download Action Types for experiences
const EXPERIENCE_DOWNLOAD_START = "EXPERIENCE_DOWNLOAD_START";
const EXPERIENCE_DOWNLOAD_UPDATE = "EXPERIENCE_DOWNLOAD_UPDATE";
const EXPERIENCE_DOWNLOAD_FINISH = "EXPERIENCE_DOWNLOAD_FINISH";

const BaseRoute = "cfwebapi/api/v1/projects";

// Reusable action types object for consistency
const actionTypes = {
  fetches: {
    request: EXPERIENCE_REQUEST,
    success: EXPERIENCES_FETCH,
    error: EXPERIENCE_ERROR,
  },
  fetch: {
    request: EXPERIENCE_REQUEST,
    success: EXPERIENCE_FETCH,
    error: EXPERIENCE_ERROR,
  },
  create: {
    request: EXPERIENCE_REQUEST,
    success: EXPERIENCE_CREATE,
    error: EXPERIENCE_ERROR,
  },
  update: {
    request: EXPERIENCE_REQUEST,
    success: EXPERIENCE_EDIT,
    error: EXPERIENCE_ERROR,
  },
  delete: {
    request: EXPERIENCE_REQUEST,
    success: EXPERIENCE_DELETE,
    error: EXPERIENCE_ERROR,
  },
  download: {
    request: EXPERIENCE_REQUEST,
    start: EXPERIENCE_DOWNLOAD_START,
    update: EXPERIENCE_DOWNLOAD_UPDATE,
    finish: EXPERIENCE_DOWNLOAD_FINISH,
    success: EXPERIENCES_FETCH,
    error: EXPERIENCE_ERROR,
  },
};

const INITIAL_STATE = {
  loading: false,
  success: false,
  exps: [],
  exp: {},
  error: "",
  downloadProgress: 0,
  isDownloading: false,
};

// Actions using the new handlers
export const FetchExperiences = () => async (dispatch) => {
  await createProgressiveRequestHandler(
    dispatch,
    () =>
      cfAPI.get(`${BaseRoute}`, {
        onDownloadProgress: createProgressHandler(
          dispatch,
          actionTypes.download.update,
          actionTypes.download.finish
        ),
      }),
    actionTypes.download,
    createProgressHandler
  );
};

export const FetchExperience = (id) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.get(`${BaseRoute}/${id}`),
    actionTypes.fetch,
    null // No toast for a fetch
  );
};

export const CreateExperience = (values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.post(BaseRoute, createFormData(values)),
    actionTypes.create,
    "Experience Created"
  );
};

export const UpdateExperience = (id, values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.patch(`${BaseRoute}/${id}`, createFormData(values)),
    actionTypes.update,
    "Experience Updated"
  );
};

export const DeleteExperience = (values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.delete(`${BaseRoute}/${values.id}`),
    actionTypes.delete,
    "Experience Deleted"
  );
};

export const ReorderExperiences = (reorderedExps) => async (dispatch) => {
  dispatch({
    type: EXPERIENCE_REORDER_LOCAL,
    payload: reorderedExps,
  });

  // Create the payload for the backend
  const reorderPayload = reorderedExps.map((item, index) => ({
    id: item.id,
    sortOrder: index,
  }));
  try {
    // Call the backend API to save the new order
    await cfAPI.post(`${BaseRoute}/reorder`, reorderPayload);
  } catch (error) {
    // If the API call fails, dispatch an error and potentially revert the state
    dispatch({
      type: EXPERIENCE_ERROR,
      payload: "Failed to save the new order.",
    });
    dispatch(FetchExperiences());
  }
};

// Reducer with toasts removed and state logic updated
export const ExperiencesReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case EXPERIENCE_DOWNLOAD_START:
      return { ...state, isDownloading: true, downloadProgress: 0 };
    case EXPERIENCE_DOWNLOAD_UPDATE:
      return { ...state, downloadProgress: action.payload };
    case EXPERIENCE_DOWNLOAD_FINISH:
      return { ...state, isDownloading: false, downloadProgress: 100 };
    case EXPERIENCE_REQUEST:
      return { ...state, loading: true };
    case EXPERIENCES_FETCH:
      return {
        ...state,
        loading: false,
        error: "",
        exps:
          action.payload && Array.isArray(action.payload) ? action.payload : [],
      };
    case EXPERIENCE_FETCH:
      return { ...state, loading: false, error: "", exp: action.payload };
    case EXPERIENCE_CREATE:
      return {
        ...state,
        loading: false,
        error: "",
        success: true,
        exps: [...state.exps, action.payload],
      };
    case EXPERIENCE_EDIT:
      const updatedList = state.exps.map((experience) =>
        experience.id === action.payload.id ? action.payload : experience
      );
      return {
        ...state,
        loading: false,
        error: "",
        success: true,
        exp: action.payload,
        exps: updatedList,
      };
    case EXPERIENCE_DELETE:
      const newList = state.exps.filter((s) => s.id !== action.payload.id);
      return {
        ...state,
        loading: false,
        error: "",
        exps: newList,
      };
    case EXPERIENCE_REORDER_LOCAL:
      return {
        ...state,
        exps: action.payload,
      };
    case EXPERIENCE_ERROR:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};
